import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Badge,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Tooltip,
  IconButton,
  Typography
} from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import Select from 'react-select';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogStandard from '@bumble/app/components/DialogStandard';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import HistoryIcon from '@mui/icons-material/History';
import ConsoleAccessHistory from '@bumble/app/components/ConsoleAccessHistory';
import { ConsoleHistoryConnection } from '@bumble/app/utils/console-access-history';
import { UsbDeviceEdit, ConnectConsoleAccess } from '@bumble/app/components/usbDevices';
import { Indicator } from '@bumble/app/components/Indicator';
import { toast } from 'react-toastify';
import { apiError } from '../utils/api-error';
import DialogConfirm from '@bumble/app/components/DialogConfirm';

export const RemoteConsoleAccess = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [historySelection, setHistorySelection] = useState<{
    connection: ConsoleHistoryConnection;
    label: string;
  } | null>(null);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [cpes, setCpes] = useState<any[]>([{ label: 'Select a CPE…', value: 'none'}]);
  const [selectedCpe, setSelectedCpe] = useState<any>(cpes[0]);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showConnectDialog, setShowConnectDialog] = useState<boolean>(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [readyUrl, setReadyUrl] = useState('');
  const [showReadyDialog, setShowReadyDialog] = useState(false);

  const infoURL = 'https://www.bumblebeenet.com/post/oob-remote-serial-console-access';

  const { data } = useQuery(
    ['routers'],
    api.getCpeApiV1GetCpeGet.bind(null, {}),
    { enabled: true }
  );

  const {
    isLoading: loadingDevices,
    data: usbDeviceData,
    refetch: usbDeviceRefetch,
    isRefetching: refetchingDevices
  } = useQuery(
    ['remote-console-access', selectedCpe?.value],
    api.getCpeUsbDevicesApiV1GetCpeUsbDevicesGet.bind(null, {
      cpe_id: selectedCpe?.value,
    }),
    { enabled: selectedCpe?.value && selectedCpe.value !== 'none' }
  );

  // Derived (not snapshotted) so it always reflects the latest fetched row —
  // e.g. right after Connect/Disconnect refetches usbDeviceData, without
  // requiring the user to reselect the row for the change to be picked up.
  const selectedDeviceData = useMemo(() => {
    if (rowSelectionModel.length !== 1) return null;
    const selectedId = rowSelectionModel[0];
    return usbDeviceData?.data?.find((d: any) => d.hw_id_path === selectedId) ?? null;
  }, [rowSelectionModel, usbDeviceData]);

  useEffect(() => {
    const entity_data = data?.data;
    let actualEntities = [{ label: 'Select a CPE…', value: 'none' }];
    if (entity_data?.length) {
      actualEntities = actualEntities.concat(entity_data.map((e: any) => ({
        label: e.cpe_name,
        value: e.cpe_id,
      })));
    }
    setCpes(actualEntities);
    if (!selectedCpe || !actualEntities.find(c => c.value === selectedCpe?.value)) {
      setSelectedCpe(actualEntities[0]);
    }
  }, [data]);

  const disconnectConsoleAccess = useMutation(
    api.disconnectConsoleAccessApiV1DisconnectConsoleAccessPost,
    {
      onSuccess() {
        toast.success('Disconnected Console Access.');
        usbDeviceRefetch();
      },
      onError: apiError,
    }
  );

  const columns: GridColDef[] = [
    { field: 'port_label', headerName: 'Port Label', width: 150 },
    {
      field: 'cable_status',
      headerName: 'USB Status',
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {<Indicator active={params.value === true} />}
        </Box>
      ),
    },
    {
      field: 'rj45_status',
      headerName: 'RJ45 Status',
      width: 150,
      renderCell: (params) => {
        if (params.value !== 'connected' && params.value !== 'disconnected') {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              {params.value}
            </Box>
          );
        }
        return <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {<Indicator active={ params.value === 'connected' } />}
        </Box>
      },
    },
    { field: 'session_status', headerName: 'Session Status', width: 150 },
    { field: 'port_description', headerName: 'Description', flex: 1, minWidth: 250 },
    { field: 'baud_rate', headerName: 'Baud Rate', width: 150 },
    { field: 'total_access_count', headerName: 'Access Count', width: 150 },
    { field: 'total_access_time', headerName: 'Total Access Time', width: 150 },
    { field: 'layout', headerName: 'USB Port Layout', width: 300 },
    { field: 'device_name', headerName: 'USB Cable Chip Name', width: 200 },
  ];

  const changeCpe = (val: any) => {
    setSelectedCpe(val);
    setHistorySelection(null);
    setRowSelectionModel([]);
    usbDeviceRefetch();
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
    if (newSelection.slice(-1)[0] !== rowSelectionModel[0]) setHistorySelection(null);
  };

  const handleDisconnect = () => {
    if (!selectedCpe?.value || !selectedDeviceData?.hw_id_path) {
      toast.error('Select a device and CPE first');
      return;
    }

    disconnectConsoleAccess.mutate({
      cpe_id: selectedCpe.value,
      hw_id_path: selectedDeviceData.hw_id_path,
    });
    setShowDisconnectDialog(false);
  };

  const handleDataRefetch = () => {
    if (selectedCpe?.value && selectedCpe.value !== 'none') {
      usbDeviceRefetch();
      toast.success('Refreshed USB device data.');
    }
  };

  const handleEditDialog = () => {
    if (!selectedDeviceData || !selectedCpe?.value) {
      toast.error('Select a device first');
      return;
    }
    setShowEditDialog(true);
  };

  const handleConnectDialog = () => {
    if (!selectedDeviceData || !selectedCpe?.value) {
      toast.error('Select a device first');
      return;
    }
    setShowConnectDialog(true);
  };

  const handleHistory = () => {
    if (!selectedDeviceData || !selectedCpe?.value || selectedCpe.value === 'none') {
      toast.error('Select a USB port first');
      return;
    }
    const connection: ConsoleHistoryConnection = {
      cpe_id: selectedCpe.value,
      access_type: 'usb',
      hw_id_path: selectedDeviceData.hw_id_path,
    };
    setHistorySelection({
      connection,
      label: `${selectedCpe.label}/${selectedDeviceData.port_label || selectedDeviceData.layout || 'USB port'}`,
    });
    queryClient.invalidateQueries(['console-access-history', connection]);
  };

  const menuItems = [
    {
      label: 'Edit',
      icon: <EditIcon color="info" fontSize="small" />,
      onClick: handleEditDialog,
    },
    {
      label: 'Connect',
      icon: <LinkIcon color="info" fontSize="small" />,
      onClick: handleConnectDialog,
    },
    {
      label: 'Disconnect',
      icon: <LinkOffIcon color="info" fontSize="small" />,
      onClick: () => setShowDisconnectDialog(true),
    },
    {
      label: 'History',
      icon: <HistoryIcon color="info" fontSize="small" />,
      onClick: handleHistory,
      disabled: !selectedDeviceData,
    },
  ];

  return (
    <PageContent>
      <PageHeader
        pageName="USB Serial Console Access"
      />
      <Box sx={{ display: 'inline-flex', mb: 5, position: 'relative', gap: 2 }}>
        <Select options={cpes} value={selectedCpe} onChange={changeCpe} styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              borderRadius: '8px',
              width: '300px',
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              width: '300px',
            }),
        }}/>
        <Badge badgeContent={0} color="secondary">
          <Box
            component="a"
            href={infoURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Tooltip arrow title="Info">
              <IconButton color="primary" size="small">
                <InfoOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Badge>
      </Box>
      {(disconnectConsoleAccess.isLoading || loadingDevices) && selectedCpe.value !== 'none' ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={
                usbDeviceData?.data?.map((item: any) => ({
                  ...item,
                  id: item.hw_id_path,
                })) || []
              }
              columns={columns}
              pagination
              rowSelectionModel={rowSelectionModel}
              handleSelectionChange={handleSelectionChange}
              itemsSelected={rowSelectionModel.length > 0}
              menuItems={menuItems}
              refreshData={handleDataRefetch}
              isRefetching={refetchingDevices}
              refreshDisabled={!selectedCpe || selectedCpe.value === 'none'}
            />
          </CardContent>
        </Card>
      )}
      {historySelection && (
        <ConsoleAccessHistory
          connection={historySelection.connection}
          label={historySelection.label}
          onClose={() => setHistorySelection(null)}
        />
      )}
      {showEditDialog && selectedDeviceData && selectedCpe && (
        <DialogStandard
          open={showEditDialog}
          handleClose={() => setShowEditDialog(false)}
          title="Edit USB Device info"
        >
          <UsbDeviceEdit
            onSubmit={() => {
              setShowEditDialog(false);
              usbDeviceRefetch();
            }}
            cpeID={selectedCpe.value}
            hwIDPath={selectedDeviceData.hw_id_path}
          />
        </DialogStandard>
      )}

      {showConnectDialog && selectedDeviceData && selectedCpe && (
        <DialogStandard
          open={showConnectDialog}
          handleClose={() => setShowConnectDialog(false)}
          title="Console Access"
        >
          <ConnectConsoleAccess
            open={showConnectDialog}
            onSubmit={() => {
              setShowConnectDialog(false);
              usbDeviceRefetch();
            }}
            onReady={(url) => {
              setReadyUrl(url);
              setShowReadyDialog(true);
            }}
            cpeID={selectedCpe.value}
            hwIDPath={selectedDeviceData.hw_id_path}
            interfaceValue={selectedDeviceData.interface}
          />
        </DialogStandard>
      )}

      {showDisconnectDialog && (
        <DialogConfirm
          open={showDisconnectDialog}
          handleClose={() => setShowDisconnectDialog(false)}
          handleConfirm={handleDisconnect}
          title="Disconnect Console Access?"
          description="Are you sure you want to disconnect this console session?"
        />
      )}

      <Dialog
        open={showReadyDialog}
        onClose={() => setShowReadyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px' }}>
          Console Access Ready
        </DialogTitle>

        <DialogContent>
          {readyUrl ? (
            <>
              <Typography sx={{ mb: 2 }}>
                Click Open Console to open the console in a new tab:
              </Typography>

              <Typography sx={{ wordBreak: 'break-all', color: 'primary.main' }}>
                {readyUrl}
              </Typography>
            </>
          ) : (
            <Typography>No URL returned from server.</Typography>
          )}
        </DialogContent>

        <DialogActions>
          {readyUrl && (
            <Button
              variant="outlined"
              onClick={() => window.open(readyUrl, '_blank', 'noopener')}
            >
              Open Console
            </Button>
          )}

          <Button variant="outlined" onClick={() => setShowReadyDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PageContent>
  );
};
